import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { useHistory, useLocation } from 'react-router';
import { Button, Typography } from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import AppLoader from '../../basic/AppLoader';
import AppTable from '../../basic/AppTable';
import { filterTypes } from '../../basic/AdvancedFilters';
import { useItems } from '../../../hooks/atoms/items-hook';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import { parseDate } from '../consents/ConsentsTable';

const tableColumns = [
  { id: 1, label: 'ID', field: 'id' },
  { id: 2, label: 'Name', field: 'name' },
  { id: 3, label: 'Surname', field: 'family_name' },
  { id: 4, label: 'Specialty', field: 'specialty' },
  { id: 5, label: 'E-mail', field: 'email' },
  { id: 6,
    label: 'Created At',
    field: 'created_at',
    customRenderer({ row }) {
      return (
        <>
          {parseDate(row.created_at)}
        </>
      );
    } },
  { id: 7,
    label: 'Status',
    field: 'verification_status',
    customRenderer({ row }) {
      return (
        <div>
          {
            'VERIFIED' === row.verification_status && (
              <Typography variant='body1' color='secondary'>Verified</Typography>
            )
          }
          {
            'UNVERIFIED' === row.verification_status && (
              <Typography
                variant='body1'
                style={ { color: '#ED6C02' } }
              >
                Not verified
              </Typography>
            )
          }
          {
            'BLOCKED' === row.verification_status && (
              <Typography variant='body1' color='error'>Blocked</Typography>
            )
          }
        </div>
      );
    } },
  { id: 8,
    label: 'ERP Uploaded',
    field: 'erp_uploaded',
    customRenderer({ row }) {
      return (
        <div>
          {
            row.erp_uploaded && (
              <DoneIcon style={ { color: '#93c120' } } />
            )
          }
          {
            !row.erp_uploaded && (
              <CloseIcon style={ { color: 'red' } } />
            )
          }
        </div>
      );
    } },
  { id: 9,
    label: 'Country',
    field: 'country' },
];

const DoctorsTable = () => {
  const [ filterQuery, setFilterQuery ] = useState({});

  const {
    items: doctors,
    itemsLoading: doctorsLoading,
    pagination,
  } = useItems(CollectionKeys.DOCTORS, { filterQuery });

  const {
    items: allDoctors,
    itemsLoading: allDoctorsLoading,
  } = useItems(CollectionKeys.DOCTORS, { filterQuery: {
    _limit: -1,
  } });

  const location = useLocation();
  const history = useHistory();

  if (doctorsLoading || allDoctorsLoading) {
    return <AppLoader />;
  }

  const downloadData = (theDoctors) => {
    if (null == theDoctors) {
      return;
    }

    let parsedAddress = {};
    const data = theDoctors.map((doctor) => {
      if (doctor.address) {
        try {
          parsedAddress = JSON.parse(doctor.address);
        } catch (e) {
          console.error('Error on JSON.parse:', e);
          parsedAddress = {};
        }
      }

      const obj = {
        ID: doctor?.id || '',
        Name: doctor?.name || '',
        Surname: doctor?.family_name || '',
        'Company name': doctor?.brand_name || '',
        'VAT ID': doctor?.tax_identification_number || '',
        'Public Sector Health Professional': doctor?.public_sector_hp || false,
        Specialty: doctor?.specialty || '',
        Address: doctor.address ? `${ parsedAddress?.streetAddress ?? '' }, ${ parsedAddress?.city ?? '' }, ${ parsedAddress?.postalCode ?? '' }` : '',
        Country: doctor.country || '',
        'E-mail': doctor?.email || '',
        'Business number': doctor.business_number || '',
        'Phone number': doctor.phone_number || '',
        'Created At': parseDate(doctor?.created_at) || '',
        Status: `${ 'VERIFIED' === doctor?.verification_status ? 'Verified' : ('UNVERIFIED' === doctor?.verification_status ? 'Not verified' : 'Blocked') }`,
        'ERP Uploaded': `${ doctor?.erp_uploaded ? 'Yes' : 'No' }`,
      };

      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = fitToColumn(data);

    function fitToColumn(arrayOfArray) {
      const MAX_WIDTH = 50;
      return Object.keys(arrayOfArray[0]).map((key) => {
        // Find the longest value in the column, including the header
        const maxLength = arrayOfArray.reduce((max, row) => {
          const value = row[key] ? row[key].toString() : ''; // Convert value to string if not null/undefined
          return Math.max(max, value.length);
        }, key.length);
        return { wch: Math.min(maxLength, MAX_WIDTH) };
      });
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'Doctors.xlsx');
  };

  const handleViewAction = (e, doctorId) => {
    history.push({
      pathname: location.pathname,
      search: `?id=${ doctorId }`,
    });
  };
  return (
    <>
      <Button
        style={ { marginRight: '10px' } }
        onClick={ () => { downloadData(doctors); } }
        variant='contained'
      >
        Download
      </Button>
      <Button
        style={ { marginRight: '10px' } }
        onClick={ () => { downloadData(allDoctors); } }
        variant='contained'
      >
        Download All
      </Button>
      <AppTable
        columns={ tableColumns }
        data={ doctors }
        loading={ doctorsLoading }
        paginationProps={ pagination }
        advancedFiltersProps={ {
          properties: [
            {
              value: 'name',
              label: 'Name',
              type: filterTypes.FREE_TEXT,
            },
            {
              value: 'family_name',
              label: 'Surname',
              type: filterTypes.FREE_TEXT,
            },
            {
              value: 'email',
              label: 'E-mail',
              type: filterTypes.FREE_TEXT,
            },
            {
              value: 'created_at',
              label: 'Created At',
              type: filterTypes.FREE_TEXT,
            },
            {
              value: 'country',
              label: 'Country',
              type: filterTypes.CATEGORY,
              options: [
                { value: 'GR', label: 'GREECE' },
                { value: 'US', label: 'USA' },
                { value: 'AL', label: 'ALBANIA' },
                { value: 'IN', label: 'INDIA' },
                { value: 'IQ', label: 'IRAQ' },
              ],
            },
          ],
          initialAndFilterOptions: [],
          onQueryChange: setFilterQuery,
        } }
        ViewProp={ { handleView: handleViewAction } }
      />
    </>
  );
};

export default DoctorsTable;

